import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ChevronLeft, Loader, Eye, CheckCircle, X,
  FileText, ClipboardList,
} from "lucide-react";
import {
  lmsGetAnswerSheetById, lmsGetExamById,
  lmsGetManualPapers, lmsGetManualPaperById,
  lmsGetSheetMarks, lmsSaveSheetMarks,
  lmsReviewAnswerSheet,
} from "../api/lmsApi";
import "../lms.css";

const Q_TYPE_LABELS = {
  mcq: "MCQ", true_false: "True / False", fill_blank: "Fill in the Blank",
  short_answer: "Short Answer", long_answer: "Long Answer", match_following: "Match the Following",
};

function MatchPairs({ text }) {
  try {
    const p = JSON.parse(text);
    if (Array.isArray(p.colA)) {
      const pairs = p.colA.map((a, i) => {
        const ansLetter = p.answers?.[i];
        const bIdx = ansLetter ? ansLetter.charCodeAt(0) - 97 : i;
        return { a, b: p.colB?.[bIdx] ?? "?" };
      });
      return (
        <div>
          {p.instruction && <div style={{ fontWeight: 600, marginBottom: 6 }}>{p.instruction}</div>}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", marginBottom: 4, letterSpacing: 0.5 }}>CORRECT PAIRS</div>
          {pairs.map((pair, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{ flex: 1, padding: "4px 10px", background: "#f0f9ff", borderRadius: 6, fontSize: 12, border: "1px solid #bae6fd", fontWeight: 600 }}>
                {i + 1}. {pair.a}
              </div>
              <span style={{ color: "#9ca3af", fontWeight: 700 }}>→</span>
              <div style={{ flex: 1, padding: "4px 10px", background: "#dcfce7", borderRadius: 6, fontSize: 12, border: "1px solid #86efac", fontWeight: 600, color: "#166534" }}>
                {pair.b}
              </div>
            </div>
          ))}
        </div>
      );
    }
  } catch {}
  return <span>{text}</span>;
}

export default function OfflineEvaluate() {
  const { examId, sheetId } = useParams();
  const nav = useNavigate();

  const [sheet,   setSheet]   = useState(null);
  const [exam,    setExam]    = useState(null);
  const [paper,   setPaper]   = useState(null);
  const [qMarks,  setQMarks]  = useState({});
  const [grade,   setGrade]   = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [tab,     setTab]     = useState("questions");

  useEffect(() => {
    const init = async () => {
      try {
        const [sheetRes, examRes] = await Promise.all([
          lmsGetAnswerSheetById(sheetId),
          lmsGetExamById(examId),
        ]);
        const s = sheetRes.data;
        const e = examRes.data;
        setSheet(s);
        setExam(e);
        setGrade(s.grade || "");
        setRemarks(s.teacher_remarks || "");

        const [papersRes, marksRes] = await Promise.all([
          lmsGetManualPapers({ exam_id: examId }),
          lmsGetSheetMarks(sheetId),
        ]);
        const papers = papersRes?.data?.data || [];
        if (papers[0]) {
          const full = await lmsGetManualPaperById(papers[0].id);
          setPaper(full.data);
        }
        const existing = marksRes?.data?.data || [];
        const m = {};
        existing.forEach((row) => {
          m[row.question_id] = { marks: String(row.marks_awarded ?? ""), remark: row.teacher_remark || "" };
        });
        setQMarks(m);
      } catch {
        toast.error("Failed to load evaluation data");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [sheetId, examId]);

  const allQuestions = (() => {
    const seen = new Set();
    return (paper?.sections?.flatMap((sec) =>
      (sec.questions || []).map((q) => ({
        ...q,
        sectionType:  sec.question_type,
        sectionLabel: sec.section_label,
        marksPerQ:    sec.marks_per_q,
      }))
    ) || []).filter((q) => {
      if (seen.has(q.id)) return false;
      seen.add(q.id);
      return true;
    });
  })();

  const computedTotal = allQuestions.reduce((sum, q) => {
    const m = parseFloat(qMarks[q.id]?.marks ?? "");
    return sum + (isNaN(m) ? 0 : m);
  }, 0);

  const setMark   = (qid, val) => setQMarks((p) => ({ ...p, [qid]: { ...p[qid], marks: val } }));
  const setRemark = (qid, val) => setQMarks((p) => ({ ...p, [qid]: { ...p[qid], remark: val } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (allQuestions.length > 0) {
        const marksArr = allQuestions.map((q) => ({
          question_id:    q.id,
          marks_awarded:  parseFloat(qMarks[q.id]?.marks ?? 0) || 0,
          teacher_remark: qMarks[q.id]?.remark || null,
        }));
        await lmsSaveSheetMarks(sheetId, { marks: marksArr });
      }
      const manualTotal = parseFloat(qMarks._manual ?? 0) || 0;
      await lmsReviewAnswerSheet(sheetId, {
        marks_obtained:  allQuestions.length > 0 ? computedTotal : manualTotal || null,
        grade:           grade || null,
        teacher_remarks: remarks || null,
      });
      toast.success("Evaluation saved!");
      nav(-1);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const isPdf   = sheet?.file_url && /\.pdf(\?|$)/i.test(sheet.file_url);
  const isImage = sheet?.file_url && /\.(jpe?g|png|webp|gif)(\?|$)/i.test(sheet.file_url);

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
      <Loader size={32} className="ms2-spin" color="#4f46e5" />
    </div>
  );

  if (!sheet) return (
    <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>Answer sheet not found.</div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>

      {/* ── Top bar ── */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "10px 20px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <button onClick={() => nav(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600 }}>
          <ChevronLeft size={16} /> Back
        </button>
        <div style={{ width: 1, height: 20, background: "#e5e7eb" }} />
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <ClipboardList size={16} color="#fff" />
        </div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>
            {sheet.student_name || `Student #${sheet.student_id}`}
          </div>
          <div style={{ fontSize: 11, color: "#6b7280" }}>
            {exam?.name} · Offline · Submitted: {sheet.submitted_at ? new Date(sheet.submitted_at).toLocaleString("en-IN") : "—"}
            {allQuestions.length > 0 && (
              <> · Score: <strong style={{ color: "#4f46e5" }}>{computedTotal.toFixed(1)} / {exam?.total_marks || "?"}</strong></>
            )}
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {sheet.file_url && (
            <a href={sheet.file_url} target="_blank" rel="noreferrer" style={{
              display: "flex", alignItems: "center", gap: 5, padding: "7px 14px",
              borderRadius: 8, background: "#f0f9ff", color: "#0369a1", border: "1.5px solid #bae6fd",
              fontSize: 12, fontWeight: 700, textDecoration: "none",
            }}>
              <Eye size={13} /> Open in New Tab
            </a>
          )}
          <button onClick={handleSave} disabled={saving} style={{
            padding: "7px 20px", borderRadius: 8, border: "none",
            background: saving ? "#a5b4fc" : "linear-gradient(135deg,#16a34a,#15803d)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            {saving ? <Loader size={13} className="ms2-spin" /> : <CheckCircle size={13} />}
            {saving ? "Saving..." : "Save Evaluation"}
          </button>
        </div>
      </div>

      {/* ── Split layout ── */}
      <div style={{ display: "flex", alignItems: "flex-start", flex: 1 }}>

        {/* LEFT — PDF / Image Viewer */}
        <div style={{ flex: "0 0 55%", borderRight: "1px solid #e5e7eb", background: "#1e1e2e", display: "flex", flexDirection: "column", position: "sticky", top: 0, height: "100vh", overflow: "hidden" }}>
          <div style={{ padding: "8px 14px", background: "#111827", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <FileText size={14} color="#a5b4fc" />
            <span style={{ fontSize: 12, color: "#a5b4fc", fontWeight: 600 }}>Student's Answer Sheet</span>
            <span style={{ fontSize: 11, color: "#6b7280", marginLeft: "auto" }}>{sheet.file_name || "uploaded file"}</span>
          </div>
          <div style={{ flex: 1, overflow: "auto", display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
            {!sheet.file_url ? (
              <div style={{ color: "#6b7280", padding: 40, textAlign: "center" }}>
                <FileText size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div>No file uploaded</div>
              </div>
            ) : isPdf ? (
              <iframe src={sheet.file_url} title="Answer Sheet" style={{ width: "100%", height: "100%", border: "none", minHeight: "calc(100vh - 48px)" }} />
            ) : isImage ? (
              <img src={sheet.file_url} alt="Answer Sheet" style={{ maxWidth: "100%", display: "block", padding: 12 }} />
            ) : (
              <div style={{ color: "#9ca3af", padding: 40, textAlign: "center" }}>
                <FileText size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div style={{ marginBottom: 12, fontSize: 13 }}>This file type cannot be previewed inline.</div>
                <a href={sheet.file_url} target="_blank" rel="noreferrer" style={{
                  display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 18px",
                  borderRadius: 9, background: "#4f46e5", color: "#fff", fontWeight: 700, fontSize: 13, textDecoration: "none",
                }}>
                  <Eye size={14} /> Download / Open File
                </a>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Grading Panel */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#f8fafc" }}>

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb", background: "#fff", flexShrink: 0 }}>
            {[
              { key: "questions", label: `All Questions (${allQuestions.length})` },
              { key: "overview",  label: "Grade & Remarks" },
            ].map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: "10px 20px 11px", border: "none", background: "none", cursor: "pointer",
                borderBottom: `3px solid ${tab === t.key ? "#4f46e5" : "transparent"}`,
                color: tab === t.key ? "#4f46e5" : "#6b7280",
                fontWeight: tab === t.key ? 700 : 500, fontSize: 13,
              }}>
                {t.label}
              </button>
            ))}
          </div>

          <div style={{ padding: "14px 16px" }}>

            {/* ── QUESTIONS TAB ── */}
            {tab === "questions" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {allQuestions.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
                    <ClipboardList size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <div>No question paper linked to this exam.</div>
                    <div style={{ fontSize: 12, marginTop: 6 }}>Switch to "Grade & Remarks" to enter total marks manually.</div>
                  </div>
                ) : allQuestions.map((q, i) => {
                  const maxMark  = parseFloat(q.marks || q.marksPerQ || 0);
                  const graded   = qMarks[q.id];
                  const hasGrade = graded?.marks !== undefined && graded.marks !== "";
                  const isCorrect = hasGrade && parseFloat(graded.marks) >= maxMark && maxMark > 0;
                  const isWrong   = hasGrade && parseFloat(graded.marks) === 0;
                  const isManual  = ["short_answer", "long_answer"].includes(q.sectionType);

                  const borderColor = isCorrect ? "#86efac" : isWrong ? "#fca5a5" : "#e5e7eb";
                  const bgColor     = isCorrect ? "#f0fdf4" : isWrong ? "#fff5f5" : "#fff";
                  const headerBg    = isCorrect ? "#dcfce7" : isWrong ? "#fee2e2" : "linear-gradient(135deg,#f8fafc,#f1f5f9)";

                  return (
                    <div key={q.id} style={{ border: `1.5px solid ${borderColor}`, borderRadius: 13, overflow: "hidden", background: bgColor }}>

                      {/* Card header */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 14px", background: headerBg, borderBottom: `1px solid ${borderColor}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{
                            width: 28, height: 28, borderRadius: 9, flexShrink: 0,
                            background: isCorrect ? "#16a34a" : isWrong ? "#dc2626" : "linear-gradient(135deg,#4f46e5,#7c3aed)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 10, fontWeight: 800, color: "#fff",
                          }}>Q{q.question_no || (i + 1)}</span>
                          <div>
                            {q.sectionLabel && <span style={{ fontSize: 10, fontWeight: 700, color: "#6b7280" }}>{q.sectionLabel} · </span>}
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af" }}>{Q_TYPE_LABELS[q.sectionType] || q.sectionType}</span>
                            <span style={{ fontSize: 10, color: "#9ca3af", marginLeft: 6 }}>[{maxMark} marks]</span>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {hasGrade && (
                            <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#dbeafe", color: "#1e40af" }}>
                              Graded: {parseFloat(graded.marks).toFixed(2)}
                            </span>
                          )}
                          {isCorrect && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: "#dcfce7", color: "#166534", display: "flex", alignItems: "center", gap: 3 }}><CheckCircle size={11} /> Correct</span>}
                          {isWrong   && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: "#fee2e2", color: "#991b1b", display: "flex", alignItems: "center", gap: 3 }}><X size={11} /> Wrong</span>}
                        </div>
                      </div>

                      <div style={{ padding: "12px 14px" }}>

                        {/* Question text */}
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 10, lineHeight: 1.7 }}>
                          {q.sectionType === "match_following"
                            ? <MatchPairs text={q.question_text} />
                            : q.question_text}
                        </div>

                        {/* Correct answer (for reference) */}
                        {q.correct_answer && q.sectionType !== "match_following" && (
                          <div style={{ marginBottom: 10, padding: "7px 12px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #86efac" }}>
                            <span style={{ fontSize: 9, fontWeight: 800, color: "#166534", letterSpacing: 0.8 }}>CORRECT ANSWER — </span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#166534" }}>{q.correct_answer}</span>
                          </div>
                        )}

                        {/* Hint for manual grading */}
                        {isManual && (
                          <div style={{ marginBottom: 10, padding: "7px 12px", background: "#fffbeb", borderRadius: 8, border: "1px solid #fde68a" }}>
                            <span style={{ fontSize: 11, color: "#92400e", fontWeight: 600 }}>See student's written answer in the PDF on the left.</span>
                          </div>
                        )}

                        {/* Remark textarea */}
                        <textarea
                          rows={2}
                          value={qMarks[q.id]?.remark ?? ""}
                          onChange={(e) => setRemark(q.id, e.target.value)}
                          placeholder="Remark for this question (optional)..."
                          style={{ width: "100%", marginTop: 0, marginBottom: 8, padding: "7px 9px", borderRadius: 7, border: "1.5px solid #e5e7eb", fontSize: 11, resize: "vertical", color: "#374151", boxSizing: "border-box" }}
                        />

                        {/* Grading row */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "9px 12px", background: "#f8fafc", borderRadius: 9, border: "1px solid #e5e7eb" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>Mark as:</span>
                          <button onClick={() => setMark(q.id, String(maxMark))} style={{
                            padding: "5px 12px", borderRadius: 7, border: "1.5px solid #16a34a",
                            background: isCorrect ? "#16a34a" : "#dcfce7",
                            color: isCorrect ? "#fff" : "#166534",
                            fontSize: 11, fontWeight: 700, cursor: "pointer",
                            display: "flex", alignItems: "center", gap: 4,
                          }}>
                            <CheckCircle size={12} /> Correct
                          </button>
                          <button onClick={() => setMark(q.id, "0")} style={{
                            padding: "5px 12px", borderRadius: 7, border: "1.5px solid #dc2626",
                            background: isWrong ? "#dc2626" : "#fee2e2",
                            color: isWrong ? "#fff" : "#991b1b",
                            fontSize: 11, fontWeight: 700, cursor: "pointer",
                            display: "flex", alignItems: "center", gap: 4,
                          }}>
                            <X size={12} /> Wrong
                          </button>
                          <div style={{ display: "flex", alignItems: "center", gap: 5, marginLeft: "auto" }}>
                            <span style={{ fontSize: 11, color: "#6b7280" }}>Marks:</span>
                            <input
                              type="number" min="0" max={maxMark || undefined} step="0.5"
                              value={qMarks[q.id]?.marks ?? ""}
                              onChange={(e) => setMark(q.id, e.target.value)}
                              placeholder="0"
                              style={{ width: 62, padding: "5px 8px", borderRadius: 7, border: "1.5px solid #d1d5db", fontSize: 13, fontWeight: 700, textAlign: "center", outline: "none" }}
                            />
                            <span style={{ fontSize: 11, color: "#9ca3af" }}>/ {maxMark || "?"}</span>
                          </div>
                        </div>

                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── GRADE & REMARKS TAB ── */}
            {tab === "overview" && (
              <div style={{ background: "#fff", borderRadius: 12, padding: 20, border: "1px solid #e5e7eb" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", display: "block", marginBottom: 5 }}>
                      TOTAL MARKS {exam?.total_marks ? `(out of ${exam.total_marks})` : ""}
                    </label>
                    <input
                      type="number"
                      readOnly={allQuestions.length > 0}
                      value={allQuestions.length > 0 ? computedTotal.toFixed(1) : (qMarks._manual ?? "")}
                      onChange={(e) => allQuestions.length === 0 && setQMarks((p) => ({ ...p, _manual: e.target.value }))}
                      style={{ width: "100%", padding: "9px 12px", borderRadius: 9, fontSize: 16, fontWeight: 800, color: "#4f46e5", background: allQuestions.length > 0 ? "#f5f3ff" : "#fff", border: "1.5px solid #e0e7ff", boxSizing: "border-box" }}
                      placeholder="Enter total marks"
                    />
                    {allQuestions.length > 0 && <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 3 }}>Auto-calculated from question marks</div>}
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", display: "block", marginBottom: 5 }}>GRADE</label>
                    <input
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                      style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: "1.5px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }}
                      placeholder="e.g. A, B+, Pass, Fail"
                    />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", display: "block", marginBottom: 5 }}>OVERALL REMARKS</label>
                  <textarea
                    rows={5}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: "1.5px solid #e5e7eb", fontSize: 13, resize: "vertical", boxSizing: "border-box" }}
                    placeholder="General feedback visible to the student..."
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
